import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { GitBranch, Loader2, ArrowUp, ArrowDown, Ban } from "lucide-react";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

function Chip({ node }) {
  const content = (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs transition-colors hover:bg-muted" data-testid={`doc-flow-chip-${node.doc_type}-${node.id}`}>
      <div>
        <p className="font-semibold text-foreground">{node.label}</p>
        <p className="font-mono text-muted-foreground">{node.number}</p>
      </div>
      <StatusPill value={node.status} />
    </div>
  );
  return node.route ? <Link to={node.route}>{content}</Link> : content;
}

/** Pack 10: reusable "Document flow" panel — shows upstream/downstream chips for any
 * transactional document, plus an optional Cancel action (blocked server-side when
 * posted downstream documents exist). */
export function DocumentFlowDialog({ docType, docId, trigger, cancelEndpoint, onCancelled }) {
  const [open, setOpen] = useState(false);
  const [flow, setFlow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/integrity/document-flow/${docType}/${docId}`)
      .then(({ data }) => setFlow(data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [open, docType, docId]);

  const cancel = async () => {
    setCancelling(true);
    try {
      await api.post(cancelEndpoint);
      toast.success("Document cancelled — GL/stock/cost effects reversed");
      setOpen(false);
      onCancelled?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div onClick={() => setOpen(true)}>{trigger}</div>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-lg" data-testid="document-flow-dialog">
        <DialogHeader><DialogTitle>Document Flow</DialogTitle></DialogHeader>
        {loading || !flow ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><ArrowUp className="h-3.5 w-3.5" /> Upstream</p>
              {flow.upstream.length === 0 ? <p className="text-xs text-muted-foreground">— none —</p> : (
                <div className="space-y-1.5">{flow.upstream.map((n) => <Chip key={n.id} node={n} />)}</div>
              )}
            </div>
            <div className="rounded-lg border-2 border-primary/40 bg-primary/5 px-3 py-2" data-testid="doc-flow-current">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-primary">This document</p>
              <p className="text-sm font-semibold text-foreground">{flow.current.label} — {flow.current.number}</p>
            </div>
            <div>
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><ArrowDown className="h-3.5 w-3.5" /> Downstream</p>
              {flow.downstream.length === 0 ? <p className="text-xs text-muted-foreground">— none —</p> : (
                <div className="space-y-1.5">{flow.downstream.map((n) => <Chip key={n.id} node={n} />)}</div>
              )}
            </div>
            {cancelEndpoint && (
              <Button variant="destructive" size="sm" disabled={cancelling} onClick={cancel} data-testid="doc-flow-cancel-button">
                {cancelling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />} Cancel this document
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function DocumentFlowButton(props) {
  return (
    <DocumentFlowDialog
      {...props}
      trigger={<Button size="sm" variant="outline" data-testid={`doc-flow-button-${props.docId}`}><GitBranch className="h-3.5 w-3.5" /></Button>}
    />
  );
}
